"use client";

import { useEffect, useRef, useState } from "react";

/* Easing constants — match brief §2.6 + the Claude Design export */
export const E_IN = "cubic-bezier(0.16, 1, 0.3, 1)";
export const E_OUT = "cubic-bezier(0.4, 0, 0.2, 1)";

/* prefers-reduced-motion hook — every animation respects this */
export function useReducedMotion(): boolean {
  const [rm, setRm] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const cb = () => setRm(mq.matches);
    mq.addEventListener("change", cb);
    return () => mq.removeEventListener("change", cb);
  }, []);

  return rm;
}

/* IntersectionObserver — fires once when element enters viewport */
export function useInView<T extends HTMLElement = HTMLDivElement>(
  opts: IntersectionObserverInit = { rootMargin: "-10% 0px -10% 0px", threshold: 0 },
): readonly [React.RefObject<T | null>, boolean] {
  const ref = useRef<T | null>(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setSeen(true);
          io.disconnect();
        }
      },
      opts,
    );
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [ref, seen] as const;
}

/* Pointer-fine (desktop with mouse) — drives custom cursor mounting */
export function usePointerFine(): boolean {
  const [fine, setFine] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(pointer: fine)");
    setFine(mq.matches);
    const cb = () => setFine(mq.matches);
    mq.addEventListener("change", cb);
    return () => mq.removeEventListener("change", cb);
  }, []);

  return fine;
}
